<template>
  <section class="desktop-menu-display" aria-label="Menú dinámico de Juliam Arcade">
    <aside class="combo-stage" :data-tone="toneFor(activeFeatureIndex)">
      <header class="stage-header">
        <div>
          <span class="stage-kicker">COMBOS PRESTIGE</span>
          <strong>COMIDA + JUEGO</strong>
        </div>
        <span v-if="features.length" class="stage-position">
          {{ position(activeFeatureIndex + 1) }} <small>/ {{ position(features.length) }}</small>
        </span>
      </header>

      <Transition name="combo-swap" mode="out-in">
        <article
          v-if="activeFeature"
          :key="activeFeature.id_token || activeFeature.name || activeFeatureIndex"
          class="combo-focus"
        >
          <span class="combo-number">#{{ position(activeFeatureIndex + 1) }}</span>
          <div class="combo-copy">
            <h2>{{ activeFeature.name }}</h2>
            <p>{{ activeFeature.detail }}</p>
          </div>
          <strong class="combo-price">{{ activeFeature.price }}</strong>
        </article>
        <article v-else key="empty-combos" class="combo-focus combo-empty">
          <h2>Combos Prestige</h2>
          <p>Próximamente nuevas partidas.</p>
        </article>
      </Transition>

      <div class="combo-footer">
        <span>CAMBIO AUTOMÁTICO · 8 S</span>
        <div class="progress-track" aria-hidden="true">
          <i :key="`combo-${activeFeatureIndex}-${animationRun}`"></i>
        </div>
      </div>
    </aside>

    <section class="categories-stage">
      <header class="categories-header">
        <div>
          <span>MENÚ COMPLETO</span>
          <h2>Elige tu siguiente nivel</h2>
        </div>
        <div class="page-status">
          <span>CATEGORÍA</span>
          <strong>{{ position(activeCategoryIndex + 1) }} / {{ position(categories.length || 1) }}</strong>
        </div>
      </header>

      <Transition name="page-swap" mode="out-in">
        <article
          v-if="activeCategory"
          :key="activeCategory.id_token || activeCategory.slug || activeCategory.name"
          class="display-category"
          :data-tone="toneFor(activeCategoryIndex)"
        >
          <header>
            <span>NIVEL {{ position(activeCategoryIndex + 1) }}</span>
            <h3>{{ activeCategory.badge_label || activeCategory.name }}</h3>
            <p v-if="activeCategory.description">{{ activeCategory.description }}</p>
          </header>

          <div
            class="display-products"
            :class="{ columns: activeCategory.products.length > 4 }"
          >
            <div
              v-for="product in activeCategory.products"
              :key="product.id_token || product.name"
              class="display-product"
              :class="{ soldout: product.availability_status === 'sold_out' }"
            >
              <div>
                <h4>{{ product.name }}</h4>
                <p>{{ productDetail(product) }}</p>
              </div>
              <strong v-if="product.availability_status !== 'sold_out'">{{ productPrice(product) }}</strong>
              <strong v-else class="soldout-label">AGOTADO</strong>
            </div>
          </div>
        </article>
        <article v-else key="empty-categories" class="display-category empty-category">
          <h3>Menú en actualización</h3>
        </article>
      </Transition>

      <footer class="categories-footer">
        <span>PRECIOS EN MXN · DISPONIBILIDAD EN TIEMPO REAL</span>
        <div class="progress-track category-progress" aria-hidden="true">
          <i :key="`category-${activeCategoryIndex}-${animationRun}`"></i>
        </div>
        <span>CAMBIO · 15 S</span>
      </footer>
    </section>
  </section>
</template>

<script>
const FEATURE_INTERVAL = 8000
const CATEGORY_INTERVAL = 15000
const DESKTOP_QUERY = '(min-width: 1121px)'

export default {
  name: 'DesktopMenuDisplay',
  props: {
    features: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    productPrice: { type: Function, required: true },
    productDetail: { type: Function, required: true },
    toneFor: { type: Function, required: true }
  },
  data() {
    return {
      activeFeatureIndex: 0,
      activeCategoryIndex: 0,
      featureTimer: null,
      categoryTimer: null,
      desktopQuery: null,
      reducedMotionQuery: null,
      animationRun: 0
    }
  },
  computed: {
    activeFeature() {
      return this.features[this.activeFeatureIndex] || null
    },
    activeCategory() {
      return this.categories[this.activeCategoryIndex] || null
    }
  },
  watch: {
    features() {
      if (this.activeFeatureIndex >= this.features.length) this.activeFeatureIndex = 0
      this.restartRotation()
    },
    categories() {
      if (this.activeCategoryIndex >= this.categories.length) this.activeCategoryIndex = 0
      this.restartRotation()
    }
  },
  mounted() {
    this.desktopQuery = window.matchMedia(DESKTOP_QUERY)
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.desktopQuery.addEventListener('change', this.restartRotation)
    this.reducedMotionQuery.addEventListener('change', this.restartRotation)
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.startRotation()
  },
  beforeUnmount() {
    this.stopRotation()
    if (this.desktopQuery) this.desktopQuery.removeEventListener('change', this.restartRotation)
    if (this.reducedMotionQuery) this.reducedMotionQuery.removeEventListener('change', this.restartRotation)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  },
  methods: {
    position(value) {
      return String(value).padStart(2, '0')
    },
    startRotation() {
      if (!this.desktopQuery || !this.desktopQuery.matches) return
      if (this.reducedMotionQuery && this.reducedMotionQuery.matches) return
      if (document.hidden) return

      if (this.features.length > 1) {
        this.featureTimer = window.setInterval(() => {
          this.activeFeatureIndex = (this.activeFeatureIndex + 1) % this.features.length
        }, FEATURE_INTERVAL)
      }

      if (this.categories.length > 1) {
        this.categoryTimer = window.setInterval(() => {
          this.activeCategoryIndex = (this.activeCategoryIndex + 1) % this.categories.length
        }, CATEGORY_INTERVAL)
      }

      this.animationRun += 1
    },
    stopRotation() {
      window.clearInterval(this.featureTimer)
      window.clearInterval(this.categoryTimer)
      this.featureTimer = null
      this.categoryTimer = null
    },
    restartRotation() {
      this.stopRotation()
      this.startRotation()
    },
    handleVisibilityChange() {
      if (document.hidden) this.stopRotation()
      else this.startRotation()
    }
  }
}
</script>

<style scoped>
.desktop-menu-display {
  --text-strong: #ffffff;
  --text-readable: #dbe3f5;
  width: min(1760px, calc(100% - 72px));
  height: 85vh;
  min-height: 660px;
  margin: 22px auto 28px;
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 3fr);
  gap: 18px;
  color: var(--text-strong);
}

.combo-stage,
.categories-stage {
  min-width: 0;
  border: 2px solid rgba(255, 255, 255, .25);
  background: #090e1c;
  box-shadow: 0 18px 52px rgba(0, 0, 0, .36);
  overflow: hidden;
}

[data-tone='cyan'] { --tone: var(--cyan); }
[data-tone='pink'] { --tone: var(--pink); }
[data-tone='lime'] { --tone: var(--lime); }
[data-tone='violet'] { --tone: var(--violet); }

.combo-stage {
  display: grid;
  grid-template-rows: auto 1fr auto;
  border-color: var(--tone);
  box-shadow: inset 0 0 42px color-mix(in srgb, var(--tone) 8%, transparent), 0 18px 52px rgba(0, 0, 0, .4);
}

.stage-header,
.categories-header,
.categories-footer {
  background: #10182b;
  border-bottom: 2px solid rgba(255, 255, 255, .18);
}

.stage-header {
  min-height: 96px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.stage-header > div,
.categories-header > div:first-child {
  display: grid;
  gap: 8px;
}

.stage-kicker,
.categories-header span,
.page-status span,
.display-category > header span,
.combo-footer,
.categories-footer {
  color: #b9c6de;
  font: 900 clamp(10px, .72vw, 13px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.stage-header > div strong {
  color: var(--tone);
  font: 900 clamp(16px, 1.35vw, 24px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
}

.stage-position {
  color: var(--text-strong);
  font: 900 clamp(24px, 2vw, 36px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: nowrap;
}

.stage-position small { color: #aeb9ce; font-size: .48em; }

.combo-focus {
  min-height: 0;
  padding: clamp(24px, 2.4vw, 42px) clamp(20px, 2.2vw, 38px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 28px;
  position: relative;
}

.combo-number {
  color: var(--tone);
  font: 900 clamp(50px, 5.4vw, 96px)/.8 ui-monospace, SFMono-Regular, Menlo, monospace;
  opacity: .92;
  text-shadow: 0 0 22px color-mix(in srgb, var(--tone) 42%, transparent);
}

.combo-copy h2 {
  margin: 0 0 22px;
  color: var(--text-strong);
  font-size: clamp(28px, 2.55vw, 48px);
  line-height: .98;
  letter-spacing: -.035em;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.combo-copy p {
  margin: 0;
  color: var(--text-readable);
  font-size: clamp(17px, 1.28vw, 24px);
  line-height: 1.42;
}

.combo-price {
  color: var(--tone);
  font: 900 clamp(36px, 3.7vw, 68px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: -.05em;
  text-shadow: 0 0 20px color-mix(in srgb, var(--tone) 32%, transparent);
  white-space: nowrap;
}

.combo-empty { justify-content: center; }

.combo-footer {
  padding: 18px 22px 20px;
  display: grid;
  gap: 12px;
  background: #10182b;
  border-top: 2px solid rgba(255, 255, 255, .18);
}

.progress-track {
  height: 7px;
  background: #26324a;
  overflow: hidden;
}

.progress-track i {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--tone);
  transform-origin: left;
  animation: countdown 8s linear forwards;
}

.categories-stage {
  display: grid;
  grid-template-rows: auto 1fr auto;
}

.categories-header {
  min-height: 96px;
  padding: 17px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.categories-header h2 {
  margin: 0;
  color: var(--text-strong);
  font-size: clamp(24px, 2.1vw, 38px);
  line-height: 1;
  text-transform: uppercase;
}

.categories-header > div:first-child > span { color: var(--cyan); }

.page-status {
  display: grid;
  gap: 6px;
  text-align: right;
}

.page-status strong {
  color: var(--lime);
  font: 900 clamp(22px, 1.9vw, 34px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
}

.display-category {
  min-height: 0;
  padding: 14px;
  display: grid;
  grid-template-rows: auto 1fr;
  background: #050913;
  overflow: hidden;
}

.display-category > header {
  min-height: 92px;
  padding: 17px 24px 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  border: 2px solid color-mix(in srgb, var(--tone) 72%, #ffffff 8%);
  border-bottom-width: 3px;
  background: color-mix(in srgb, var(--tone) 11%, #121a2e);
}

.display-category > header span { color: var(--tone); }

.display-category h3 {
  margin: 0;
  color: var(--text-strong);
  font-size: clamp(28px, 2.55vw, 48px);
  line-height: 1;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}

.display-category > header p {
  margin: 0;
  color: var(--text-readable);
  font-size: clamp(13px, 1vw, 18px);
}

.display-products {
  min-height: 0;
  padding: 12px 24px 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  border: 2px solid #39445c;
  border-top: 0;
  background: #101729;
}

.display-products.columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(0, 1fr);
  column-gap: 34px;
  align-items: stretch;
}

.display-product {
  min-width: 0;
  padding: clamp(11px, 1.05vh, 18px) 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #3b465e;
}

.display-product:last-child { border-bottom: 0; }
.display-product.soldout { opacity: .72; }

.display-product h4 {
  margin: 0;
  color: var(--text-strong);
  font-size: clamp(19px, 1.48vw, 28px);
  line-height: 1.08;
}

.display-product p {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  margin: 5px 0 0;
  color: var(--text-readable);
  font-size: clamp(13px, .98vw, 18px);
  line-height: 1.3;
}

.display-product > strong {
  color: var(--tone);
  font: 900 clamp(24px, 2vw, 38px)/1 ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: nowrap;
}

.display-product > .soldout-label {
  color: #ffffff;
  background: #d12d72;
  padding: 7px 9px;
  font-size: clamp(10px, .7vw, 13px);
}

.empty-category {
  place-items: center;
  border: 2px solid #39445c;
}

.categories-footer {
  min-height: 50px;
  padding: 14px 20px;
  display: grid;
  grid-template-columns: auto minmax(80px, 1fr) auto;
  align-items: center;
  gap: 18px;
  border-top: 2px solid rgba(255, 255, 255, .18);
  border-bottom: 0;
}

.category-progress i {
  background: var(--cyan);
  animation-duration: 15s;
}

.combo-swap-enter-active,
.combo-swap-leave-active,
.page-swap-enter-active,
.page-swap-leave-active {
  transition: opacity .42s ease, transform .42s ease;
}

.combo-swap-enter-from { opacity: 0; transform: translateY(24px); }
.combo-swap-leave-to { opacity: 0; transform: translateY(-18px); }
.page-swap-enter-from { opacity: 0; transform: translateX(28px); }
.page-swap-leave-to { opacity: 0; transform: translateX(-24px); }

@keyframes countdown {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}

@media (max-width: 1360px) {
  .desktop-menu-display {
    width: calc(100% - 40px);
    grid-template-columns: minmax(260px, 1fr) minmax(0, 3fr);
    gap: 12px;
  }

  .display-category { padding: 10px; }
  .display-category > header { min-height: 78px; padding-inline: 18px; }
  .display-products { padding-inline: 18px; }
  .display-products.columns { column-gap: 24px; }
}

@media (max-width: 1120px) {
  .desktop-menu-display { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .progress-track i { animation: none; transform: scaleX(1); }
  .combo-swap-enter-active,
  .combo-swap-leave-active,
  .page-swap-enter-active,
  .page-swap-leave-active { transition: none; }
}
</style>
